// Pure helpers for the Guv POP Tag in-app read (Session AND). "POP" is Proof Of Presence; the
// feature was called "Tapping For Tracking" through Session AMZ and the underlying model is still
// AttendanceTap. DOM-free apart from one feature-detection helper, so the parsing unit-tests
// directly in the node environment, the same discipline server/src/modules/public/publicTap.ts and
// src/lib/rfid.ts already follow.
//
// WHAT THIS IS FOR. Session AMZ made a tap prove WHEN a lift was attended. It proved WHO only by
// accident: the sticker's URL is opened by the phone's OS in whatever browser it prefers, and that
// browser's cookie jar need not be the installed PWA's, so `byUser` was frequently blank. Reading
// the same sticker from INSIDE the engineer app changes that, because a live session is a
// precondition of the read rather than a coincidence of it.
//
// THE SECURITY RULE THAT SHAPES THIS WHOLE FILE, and it is new to the platform rather than
// inherited: this is the FIRST time ELE parses attacker-controllable bytes inside an authenticated
// engineer session. The field NTAG213s are rewritable (nothing on this platform ever calls
// makeReadOnly, and rfidTagStatus's "programmed" state is never set by anything), so somebody who
// gets into a plant room can rewrite a sticker to carry any URL they like.
//
//   SO: EXTRACT A TOKEN AND DISCARD THE URL. The scanned string never reaches window.location, an
//   href, a src, dangerouslySetInnerHTML, or the rendered page in any form. The ONLY thing that
//   escapes this module from a scanned tag is a 32-hex token that has already matched an anchored
//   pattern on the pathname of a successfully parsed URL. A hostile tag can therefore make the app
//   POST to a lift that is not the one in front of the engineer -- which is exactly what the OS
//   navigation path already allowed, and is mitigated by showing the recorded lift's reference back
//   to them -- but it cannot make the app navigate anywhere, render anything, or reach any path of
//   the attacker's choosing. Pinned by popTag.test.ts.
import type { PublicTapResult, Role, User } from "./api";

// The token shape, mirroring `looksLikeTapToken` (server/src/modules/public/publicTap.ts): exactly
// 32 hex characters, 16 random bytes hex-encoded.
//
// NOT the RFID validator, and the difference is load-bearing rather than cosmetic. The customer RFID
// token ALSO accepts a 20-character legacy V1 shape, because migrated NTAG213 stickers physically
// exist and must keep resolving. The POP Tag namespace was born in V2 and has no legacy, so
// widening it here would accept 80-bit tokens that nothing will ever mint. A parity test asserts
// this pattern still matches the server's.
export const POP_TAG_TOKEN_RE = /^[a-f0-9]{32}$/i;

// The anchored pathname patterns. Anchored at both ends deliberately: an unanchored match would let
// a hostile tag carrying https://evil.example/redirect?next=/tap/<32 hex> satisfy the check.
const TAP_PATH_RE = /^\/tap\/([a-f0-9]{32})$/i;
const RFID_PATH_RE = /^\/nfc\//i;

// The structural shape of an NDEF record, mirroring the real API exactly (see src/webnfc.d.ts). It
// is declared structurally rather than as the global NDEFRecord so this module stays a leaf that
// unit-tests under node with no ambient DOM, while remaining assignable FROM a real NDEFRecord.
//
// `data` is `DataView | null` and `encoding` is `string | null` because that is what Chrome
// delivers. A fixture using a plain string or a Uint8Array is a shape the browser cannot produce,
// and this repo has been bitten by exactly that class of impossible fixture twice (Session AMY's
// asset mock with no `site` key, Session AMZ's MCP mocks with no attendanceTap delegate).
export interface NdefRecordLike {
  readonly recordType: string;
  readonly encoding?: string | null;
  readonly data?: DataView | null;
  toRecords?: () => readonly NdefRecordLike[];
}

export type PopTagScan =
  // A POP Tag. The token is the only thing that crosses this boundary.
  | { kind: "pop"; token: string }
  // The CUSTOMER RFID sticker in the lift car, not the motor-room POP Tag. Its own case because it
  // is a likely and specific field mistake with an obvious remedy, and folding it into a generic
  // failure would have an engineer re-tapping the wrong sticker until they gave up.
  | { kind: "rfid" }
  // An unformatted tag: the spec says a reader SHOULD present one as a message with zero records,
  // which maps onto the platform's own "not programmed" tag status.
  | { kind: "blank" }
  // Readable, but not one of ours: no URL record, an undecodable payload, a URL that does not parse,
  // or a path that is neither /tap/ nor /nfc/.
  | { kind: "unreadable" };

// Decode one record's payload to a string, or null when it cannot be decoded.
//
// The URI prefix byte is expanded by the browser before it reaches us (the spec's parsing steps
// append the buffer to the expansion of the prefix byte, e.g. 0x04 -> "https://"), so `data` is
// already the complete URL and there is no prefix to reassemble by hand.
function decodeRecord(record: NdefRecordLike): string | null {
  const data = record.data;
  if (!data) return null;
  try {
    return new TextDecoder(record.encoding ?? "utf-8").decode(data);
  } catch {
    // An encoding label the platform does not know, or a malformed buffer. Not worth distinguishing
    // from any other unreadable tag.
    return null;
  }
}

// Classify one decoded string. Returns null when it is not a URL we recognise at all, so the caller
// can keep looking through the remaining records rather than stopping on the first miss.
function classify(decoded: string): PopTagScan | null {
  let url: URL;
  try {
    url = new URL(decoded);
  } catch {
    return null;
  }
  const tap = TAP_PATH_RE.exec(url.pathname);
  // Re-checked against the EXPORTED constant rather than trusted from the path pattern. The two
  // express the same rule, and running the captured token through the one the parity test pins is
  // what stops the path pattern quietly widening on its own: without this, TAP_PATH_RE's inline
  // character class was the real gate and nothing was watching it.
  if (tap && POP_TAG_TOKEN_RE.test(tap[1])) return { kind: "pop", token: tap[1] };
  if (RFID_PATH_RE.test(url.pathname)) return { kind: "rfid" };
  return null;
}

// The record types that can carry a URL.
//
//   "url"          a well-known-type "U" record, which is what a writer app's Link function produces
//   "absolute-url" the TNF 0x03 variant some writer apps emit instead
//   "text"         an engineer who used the writer's Text function rather than its Link function;
//                  the payload is still the URL string, so accepting it costs nothing and saves a
//                  re-programming trip to a motor room
const URL_RECORD_TYPES = new Set(["url", "absolute-url", "text"]);

// Walk an NDEF message and pull out what it is.
//
// ITERATES EVERY RECORD, NEVER records[0]. A tag written by a consumer app routinely carries an
// Android Application Record ("android.com:pkg") alongside the URL, and it may come first; a smart
// poster nests the URI inside itself and is unpacked with toRecords(). Reading index 0 is the single
// most likely way this fails in the field, and it would fail on tags that are already programmed and
// stuck to lifts.
//
// The ORIGIN is deliberately not checked. The server resolves purely on the token and never sees the
// origin, tags may legitimately carry an origin from before a domain change, and a hostile tag can
// set any origin it likes anyway, so an origin check would reject honest tags without excluding a
// single hostile one. The safety comes from the anchored PATH match and the strict token shape.
export function popTagFromRecords(records: readonly NdefRecordLike[] | undefined): PopTagScan {
  if (!records || records.length === 0) return { kind: "blank" };

  // A /tap/ hit anywhere in the message wins outright; an /nfc/ hit is remembered but kept second,
  // so a tag carrying both (which nothing produces today, but is cheap to be right about) resolves
  // to the POP Tag rather than to a wrong-sticker message.
  let rfidSeen = false;

  const visit = (list: readonly NdefRecordLike[], depth: number): PopTagScan | null => {
    // Bound the recursion. A smart poster nests one level in practice; anything deeper is either
    // malformed or hostile, and neither deserves an unbounded walk.
    if (depth > 3) return null;
    for (const record of list) {
      if (URL_RECORD_TYPES.has(record.recordType)) {
        const decoded = decodeRecord(record);
        if (decoded) {
          const hit = classify(decoded);
          if (hit?.kind === "pop") return hit;
          if (hit?.kind === "rfid") rfidSeen = true;
        }
      }
      // Unpack a smart poster or an external record that wraps others.
      if (typeof record.toRecords === "function") {
        let nested: readonly NdefRecordLike[] | undefined;
        try {
          nested = record.toRecords();
        } catch {
          nested = undefined;
        }
        if (nested && nested.length > 0) {
          const hit = visit(nested, depth + 1);
          if (hit) return hit;
        }
      }
    }
    return null;
  };

  const found = visit(records, 0);
  if (found) return found;
  if (rfidSeen) return { kind: "rfid" };
  return { kind: "unreadable" };
}

// True when this browser can read a tag at all. Web NFC is Chrome on Android only, by explicit
// Director decision that the engineer fleet is Android; there is no fallback path and none should be
// invented. The check is the repo's own idiom (compare isPushSupported in src/lib/pushClient.ts).
//
// It tells you the BROWSER supports Web NFC, never that the phone has an NFC antenna or that the
// radio is switched on. Both of those surface only as a scan() rejection, which is why the scan
// error copy below still has to cover them.
export function isPopTagScanSupported(): boolean {
  return typeof window !== "undefined" && "NDEFReader" in window;
}

// The control this module tells the engineer to press again, when nobody says otherwise. It was
// written for the standalone Scan POP Tag screen (PopTagScanPage.tsx), whose button reads "Scan POP
// Tag", so every failure told the engineer to "press Scan again" regardless of who was asking.
//
// Session APF gave the job sheet its own in-flow tap step (PopTagStep.tsx, via usePopTagScan.ts's
// own `actionLabel` option of the same name), whose button reads "Tap the tag", and this module's
// copy kept saying "press Scan" on it too -- a control name that is not on that screen. Exported so
// usePopTagScan.ts's own default can never drift from the one this file falls back to: one string,
// not two that happen to agree today.
export const DEFAULT_POP_TAG_ACTION_LABEL = "press Scan";

// Capitalises the first letter only, for the two failure messages below that open a sentence with
// the action label rather than tucking it after "then". `actionLabel` itself is always passed lower
// case, as it reads mid sentence ("then tap the tag again"); this is the one place it is read back
// out at the start of one.
function capitaliseFirst(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// What the engineer is told when a tag will not read. Keyed on the DOMException name, because the
// message text is not stable across Chrome versions.
//
// NotSupportedError AND NotReadableError DELIBERATELY SHARE ONE MESSAGE, and this is the one place
// where being precise about names is actively harmful. The spec divides them (no adapter versus an
// adapter the browser may not use) but Chromium's actual mapping does not line up with it, and
// Chromium has been observed raising NotReadableError for both. Telling an engineer their phone has
// no NFC hardware on the strength of one exception name would send them to buy a phone they already
// own. A message that is correct under either mapping is the honest answer.
//
// `actionLabel` DEFAULTS TO "press Scan" so every existing caller (the standalone screen, and every
// test that calls this with one argument) reproduces the exact original wording, byte for byte.
export function popTagScanErrorMessage(errorName: string | undefined, actionLabel: string = DEFAULT_POP_TAG_ACTION_LABEL): string {
  switch (errorName) {
    case "NotAllowedError":
      return `NFC permission was refused. Allow NFC for this site in Chrome, then ${actionLabel} again.`;
    case "NotSupportedError":
    case "NotReadableError":
      return `NFC is not available. Check NFC is switched on in Android settings, then ${actionLabel} again.`;
    case "InvalidStateError":
      return `A scan was already running. ${capitaliseFirst(actionLabel)} to try again.`;
    default:
      return `The tag could not be scanned. ${capitaliseFirst(actionLabel)} and hold the phone against the tag again.`;
  }
}

// What a scanned-but-unusable tag means, in the engineer's words.
export function popTagScanMessage(kind: Exclude<PopTagScan["kind"], "pop">): string {
  switch (kind) {
    case "rfid":
      return "That is the customer RFID sticker from the lift car, not the POP Tag. The POP Tag is on the paper log card in the motor room.";
    case "blank":
      return "That tag has not been programmed yet. Ask the office to send you its URL.";
    case "unreadable":
      return "That tag is not a POP Tag. Check you are holding the phone against the sticker on the log card.";
  }
}

// ---------------------------------------------------------------------------
// Who may be recorded
// ---------------------------------------------------------------------------

// The roles the SERVER will accept as the identity on a tap, mirroring `isStaffLoginRole`
// (server/src/modules/auth/staffLogin.ts). A positive allow-list, which CLAUDE.md's AUTH-GUARD RULE
// requires a new surface to decide rather than inherit.
//
// THE AUDITOR EXCLUSION IS THE POINT OF THIS EXISTING AT ALL, and it is not theoretical. An external
// assessor holds an ordinary session, and `isAuditorAllowedPath`
// (server/src/modules/auth/guards.ts) puts "/api/auth/me" in AUDITOR_SESSION_PATHS, so an in-window
// auditor gets a clean 200 from the preflight. A preflight that checked only the HTTP status would
// therefore FAIL OPEN for the one role the server is going to refuse: the scan would be waved
// through, the tap would record anonymously, and the engineer screen would report a failure it had
// just told the user could not happen. Checking the role here makes the client's prediction agree
// with the server's decision instead of contradicting it.
export const POP_TAG_IDENTITY_ROLES: ReadonlySet<Role> = new Set<Role>(["engineer", "office", "admin"]);

export function canRecordPopTagIdentity(user: Pick<User, "role"> | null | undefined): boolean {
  return user != null && POP_TAG_IDENTITY_ROLES.has(user.role);
}

// ---------------------------------------------------------------------------
// What actually got recorded
// ---------------------------------------------------------------------------

// The five things that can be true after an in-app scan has POSTed.
//
// THE ONE THAT JUSTIFIES THE WHOLE SESSION is `recorded_anonymous`. On a FRESH tap the route sets
// `mine: actor != null` and `signedInAs: mine ? actor.displayName : null`, and User.displayName is a
// non-nullable String, so ON A FRESH TAP a falsy signedInAs means the row was written with userId
// null. That makes this a certain read-back of the row just written, not a guess: the app does not
// have to trust its own preflight, it can see what the server actually stored. This is what turns a
// probabilistic client-side guarantee into an honest one.
//
// AND THE LIMIT OF THAT, which must not be glossed: the read-back is certain ONLY on a fresh tap. On
// a COLLAPSED tap `mine` compares the EXISTING row's userId against the caller, so a perfectly
// signed-in engineer landing on an anonymous row and one landing on somebody else's row both get
// signedInAs null, and the response cannot tell them apart from an identity failure. That is why the
// three collapsed outcomes below are separate cases with their own honest copy rather than being
// folded into the two fresh ones.
// SIX SINCE SESSION AOE, and the sixth is a different KIND of event rather than a sixth shade of
// the same one. install_confirmed is the tap that proved a freshly fitted or rotated sticker reads.
//
// EXTENDING THIS WAS A DELIBERATE DECISION, FLAGGED RATHER THAN QUIET, because the AOE brief put
// this module out of scope and asked that any reach into it be reported. It has to be extended, and
// leaving it alone would have been the defect: the very first in-app scan of a new sticker is the
// commonest way an install confirmation is made, and without this case that scan reports "Attendance
// recorded. Your visit is on file against this lift, in your name" for a row the platform has
// deliberately excluded from every count. This module's whole subject is the quiet over-claim, so
// shipping the confirmation branch on the route and not here would have installed a new one on the
// exact surface written to prevent them.
export type PopTagOutcome =
  | "install_confirmed"
  | "recorded_named"
  | "recorded_anonymous"
  | "collapsed_mine"
  | "collapsed_other"
  | "collapsed_anonymous";

export function popTagOutcome(result: PublicTapResult): PopTagOutcome {
  // FIRST, BEFORE ANY IDENTITY QUESTION, because on a confirmation the identity question is not
  // merely secondary, it is the wrong question: no attendance was recorded for anybody, so both
  // "recorded in your name" and "recorded without your name" would be false, and the anonymous one
  // would additionally send the engineer off to sign in and scan again to fix nothing.
  //
  // `=== true`, not truthiness: the field is optional on the wire (the SPA and the API deploy
  // separately, so a new bundle can reach a server that predates it) and absent must degrade to the
  // pre-AOE reading, which is what that server actually recorded.
  if (result.isInstallConfirmation === true) return "install_confirmed";
  // Truthiness, not `!== null`. An empty displayName would slip through a null check and report a
  // nameless row as a named one, which is the exact misreport this session exists to end.
  const named = Boolean(result.signedInAs);
  if (!result.alreadyRecorded) return named ? "recorded_named" : "recorded_anonymous";
  if (named) return "collapsed_mine";
  return result.recordedUnderAnotherName ? "collapsed_other" : "collapsed_anonymous";
}

// True when the office should be shown this as a problem rather than a success. Kept beside the
// outcome so a new outcome cannot be added without deciding which side of the line it falls.
export function popTagOutcomeIsClean(outcome: PopTagOutcome): boolean {
  // install_confirmed IS CLEAN. Nothing went wrong: the engineer fitted a sticker, it reads, and the
  // platform classified the tap correctly. Rendering it amber would tell them to fix something that
  // is already right, and would make the ordinary first scan of every newly tagged lift look like a
  // fault the office should chase.
  return outcome === "install_confirmed" || outcome === "recorded_named" || outcome === "collapsed_mine";
}

export interface PopTagOutcomeCopy {
  heading: string;
  body: string;
}

// The words for each outcome. In the lib rather than in the JSX so they can be pinned by a test:
// two of them exist precisely to stop a quiet misreport, and a misreport is a wording defect.
//
// THREE OF THESE DELIBERATELY DIFFER FROM THE STICKER PAGE'S OWN COPY, and a future session should
// not "align" them. The sticker page describes a passive open by whoever happened to walk past. This
// screen describes a deliberate press of a button that said it would record a NAMED attendance, so
// the same outcome means something different to the person reading it. In particular the sticker
// page's "Your attendance is covered by that record" is right there and wrong here: an engineer who
// asked to be recorded by name has NOT been, and telling them they are covered is the same class of
// quiet over-claim this session exists to end.
//
// NONE OF THEM NAMES THE OTHER ENGINEER. The route does not return the other name, on purpose, and
// no wording here may imply the page knows it. Nor may any of them say "your earlier tap": the
// collapse is keyed on the lift alone, so the row may belong to somebody else entirely.
//
// THE "few minutes" IN THE TWO RECOVERY LINES IS LOAD-BEARING, not filler. The server collapses
// repeat taps of one lift for three minutes, so an engineer who scans again immediately gets the
// same answer and concludes the feature is broken. Shortening this to "scan again" makes it wrong.
export const POP_TAG_OUTCOME_COPY: Record<PopTagOutcome, PopTagOutcomeCopy> = {
  // NO LOG CARD LINE, and no offer to scan again, unlike every other outcome here. Both would be
  // wrong: there is no visit to sign for, and scanning again inside the window returns this same
  // answer while scanning again after it records a visit that has not happened.
  install_confirmed: {
    heading: "Sticker confirmed working",
    body:
      "This tag is now live and reads correctly. That first scan proves it works and is not counted as a visit, " +
      "so the record stays honest about when somebody was actually here. Your next scan records a visit as normal.",
  },
  recorded_named: {
    heading: "Attendance recorded",
    body: "Your visit is on file against this lift, in your name. Please sign the log card as usual.",
  },
  recorded_anonymous: {
    heading: "Recorded, but without your name",
    body:
      "The visit is on file, but the record does not name you, so your sign-in did not reach the server. " +
      "Sign in again, then scan once more in a few minutes. The log card is still the record of who attended.",
  },
  collapsed_mine: {
    heading: "Already recorded",
    body: "You scanned this lift moments ago and that record names you. Nothing further is needed.",
  },
  collapsed_other: {
    heading: "Not recorded separately",
    body:
      "This lift was scanned moments ago by somebody else, so your own attendance has not been recorded. " +
      "Scan again in a few minutes if you need your own record. Please sign the log card either way.",
  },
  collapsed_anonymous: {
    heading: "Already recorded, without a name",
    body:
      "This lift was scanned moments ago and that record carries no name, so scanning again now will not add yours. " +
      "Try again in a few minutes. Please sign the log card as usual.",
  },
};
